// src/app/app.component.ts
import { afterNextRender, Component, inject, OnInit, PLATFORM_ID, NgZone, ApplicationRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet
} from '@angular/router';
import { ThemeService } from './services/theme.service';
import { NotificationService } from './services/notification.service';
import { TranslationService } from './services/translation.service';
import { SHARED_IMPORTS } from './shared/shared-imports'; // Asumo que esto contiene CommonModule y otros necesarios
import { concat, filter, first, interval } from 'rxjs';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ToastServiceService } from './services/toast-service.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, SHARED_IMPORTS],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  title = 'Chats Random!'; 
  loading = false;
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Inyectar servicios necesarios para este componente o para pasar a otros
  private theme = inject(ThemeService);
  private notifications = inject(NotificationService); // Necesario para llamar a initAuthListener
  private translation = inject(TranslationService);
  private ngZone = inject(NgZone); // Necesario para runOutsideAngular
  private router = inject(Router); // Necesario para suscribirse a eventos de navegación
  private appRef = inject(ApplicationRef); // Inyectar ApplicationRef
  private swUpdate = inject(SwUpdate); 

  constructor() {
     // --- Spinner de navegación (sin cambios) ---
    this.router.events.subscribe(e => {
      if (e instanceof NavigationStart) this.loading = true;
      if (e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError) {
        this.loading = false;
      }
    });

    // --- Lógica de Actualización Automática del Service Worker ---
    if (this.isBrowser && this.swUpdate.isEnabled) {
        console.log('[SW] Service Worker está habilitado. Configurando actualizaciones automáticas.');
        this.setupAutomaticSwUpdates();
    } else if (this.isBrowser && !this.swUpdate.isEnabled) {
        console.log('[SW] Service Worker NO está habilitado (probablemente entorno dev sin SW).');
    }

    // SSR-safe: registramos SW y escuchamos auth sólo tras primer render
    // Usar afterNextRender es la forma correcta en Angular v17+ para código dependiente del navegador
    afterNextRender(() => {
      // Ejecutar fuera de la zona para no afectar rendimiento con el registro del SW
      // o listeners de larga duración como el de auth.
      this.ngZone.runOutsideAngular(() => {
        if (this.isBrowser && 'serviceWorker' in navigator) {
          navigator.serviceWorker
            // Registrar en el root scope es lo más común y flexible
            .register('/firebase-messaging-sw.js', { scope: '/' })
            .then(reg => console.log('[SW] FCM registrado con scope:', reg.scope))
            .catch(err => console.error('[SW] Error al registrar FCM SW:', err));
        }
        const unsubAuthListener = this.notifications.initAuthListener();
      });
    });
  }

  async ngOnInit(): Promise<void> {
    // Inicializaciones que solo deben ocurrir en el navegador
    if (!this.isBrowser) {
      return;
    }
    // Inicializamos idioma y tema (Lógica existente está bien)
    try {
        await this.translation.initialize();
        this.theme.initTheme();
    } catch (error) {
        console.error("Error durante la inicialización en ngOnInit (browser):", error);
    }
  }

  navigateTo(url: string | undefined): void {
    // Solo navegar si estamos en el navegador y hay una URL válida
    if (this.isBrowser && url) {
      console.log(`[AppComponent] Navegando a la URL del toast: ${url}`);
      this.router.navigateByUrl(url)
        .catch(err => console.error(`[AppComponent] Error al navegar a ${url}:`, err));
    } else if (!url) {
        console.warn("[AppComponent] Intento de navegación sin URL desde el toast.");
    }
  }

  private setupAutomaticSwUpdates(): void {
    // 1. Comprobar actualizaciones periódicamente (opcional pero recomendado)
    // Comprueba cada 6 horas (ajusta el tiempo según necesites)
    const everySixHours$ = interval( 1000*60*5); // 6 horas en ms
    // Comprueba también cuando la aplicación se estabiliza después del inicio
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
    // Ejecuta la comprobación inicial y luego las periódicas
    concat(appIsStable$, everySixHours$).subscribe(async () => {
        try {
            console.log('[SW] Buscando actualizaciones...');
            const updateFound = await this.swUpdate.checkForUpdate();
            console.log(updateFound ? '[SW] Nueva versión detectada durante la comprobación.' : '[SW] No se encontraron nuevas versiones durante la comprobación.');
        } catch (err) {
            console.error('[SW] Error al buscar actualizaciones:', err);
        }
    });


    // 2. Escuchar cuando una nueva versión esté lista para activarse
    this.swUpdate.versionUpdates.pipe(
      filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
    ).subscribe(evt => {
      console.log(`[SW] Nueva versión ${evt.latestVersion.hash} lista para activar. Versión actual: ${evt.currentVersion.hash}`);
      // Activar la nueva versión inmediatamente
      this.swUpdate.activateUpdate().then(() => {
        console.log('[SW] Nueva versión activada. Recargando la página...');
        // Forzar la recarga de la página para usar la nueva versión
        document.location.reload();
      }).catch(err => {
        console.error('[SW] Error al activar la nueva versión:', err);
      });
    });    

    // 3. Escuchar cuando una version ha sido activada
    this.swUpdate.versionUpdates.pipe(
      filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
    ).subscribe(evt => {
        console.log(`[SW] Nueva versión ${evt.latestVersion.hash} activada. Versión actual: ${evt.currentVersion.hash}`);
        // Aquí podrías mostrar un mensaje al usuario para que recargue la página
        // alert('Una nueva versión está disponible. Por favor, recarga la página.');
    });


    // (Opcional) Escuchar errores de versión no recuperables
    this.swUpdate.unrecoverable.subscribe(event => {
        console.error(
            '[SW] Error irrecuperable detectado. Razón:',
            event.reason,
            'Se recomienda recargar la página.'
        );
        // Podrías mostrar un mensaje al usuario aquí para que recargue manualmente
        // alert('Se produjo un error con la versión de la aplicación. Por favor, recarga la página.');
    });
  }


}